<template>
  <app-page-wrapper
    class="mb-4 !max-w-[calc(theme(screens.3xl)+theme(spacing.24))] !px-4 sm:mb-6 sm:!px-6 md:mb-10 md:!px-10"
    tag="footer"
  >
    <div class="bg-primary-950 overflow-hidden rounded-xl text-white">
      <div
        class="grid gap-8 p-6 md:p-12 lg:grid-cols-[repeat(var(--columnCount),1fr)]"
        :style="{
          '--columnCount': sections.length + 1,
        }"
      >
        <div class="mr-8">
          <app-image
            alt="Pollen logo"
            class="mb-6"
            src="/images/logo_full_white.svg"
          />

          <template v-if="!properties.hideQualiopi">
            <p class="text-sm">
              Organisme de formation professionnelle et continue enregistré sous
              le numéro 11922636792.
            </p>

            <app-image
              alt="Qualiopi logo"
              class="my-4"
              src="/images/qualiopi_logo.svg"
            />

            <p class="text-sm">
              <a class="underline" :href="qualiopiCertificate" target="_blank"
                >La certification qualité</a
              >
              a été délivrée au titre de la catégorie d’actions suivante :
              ACTIONS DE FORMATION
            </p>
          </template>

          <p class="my-4 flex items-center gap-2 text-sm">
            <a :href="linkedin" target="_blank">
              <app-icon icon="mdi:linkedin" />
            </a>
            <app-divider class="!h-4 border-white/50" vertical />
            contact@heypollen.com
          </p>
        </div>

        <section v-for="section in sections" :key="section.title">
          <h3 class="mb-4 text-xl font-bold">
            {{ section.title }}
          </h3>

          <ul class="grid gap-2">
            <li v-for="item in section.items" :key="item.label">
              <nuxt-link
                active-class="!opacity-100 font-semibold"
                class="block opacity-70 transition hover:translate-x-1 hover:opacity-100"
                :href="item.href"
                :target="item.href ? '_blank' : undefined"
                :to="item.to"
              >
                {{ item.label }}
              </nuxt-link>
            </li>
          </ul>
        </section>
      </div>

      <div class="bg-primary-900 px-6 py-4 md:px-12 lg:text-center">
        <app-icon
          class="mr-1 align-top"
          icon="twemoji:envelope-with-arrow"
          :size="20"
        />
        Recevez toute l’actualité Pollen dans votre boîte mail.
        <growth-contact-button
          color="dark"
          origin="main_footer"
          origin_page_action="newsletter_subscription_cta"
          variant="link"
        >
          M'inscrire à la newsletter.
        </growth-contact-button>
      </div>
    </div>

    <p class="col-end-(--columnCount) col-start-1 mt-8 text-center text-sm">
      {{ copyrightMention }}
    </p>
  </app-page-wrapper>
</template>

<script lang="ts" setup>
import type { I18nMessage } from "@pollen/core/model/i18n.model";

import type { RouteLocationRaw } from "#vue-router";
import { useCopyrightMention } from "~/layouts/_includes/composables/main-footer.hook";

const properties = defineProps<{
  hideQualiopi?: boolean;
}>();

const copyrightMention = useCopyrightMention();
const {
  public: {
    apps: { jobs: jobsUrl },
    documents: { qualiopiCertificate, pressKit },
    socialMediasUrls: { linkedin },
  },
} = useRuntimeConfig();

const { data: categoryPages } = useCategoryPageList();

const hrClubDialogEnabled = useFeatureFlag("hr_club_dialog");

const sections = computed<
  {
    title: I18nMessage;
    items: { label: I18nMessage; to?: RouteLocationRaw; href?: string }[];
  }[]
>(() => {
  return [
    {
      title: "Entreprises",
      items: [
        {
          label: "Comment ça marche",
          to: { name: "enterprise" },
        },
        {
          label: "Intra-entreprise",
          to: { name: "formations" },
        },
      ],
    },
    {
      title: "Thématiques",
      items: categoryPages.value.map((categoryPage) => {
        return {
          label: `Formations ${categoryPage.tag.label}`,
          to: categoryPage._links.page,
        };
      }),
    },

    {
      title: "Ressources",
      items: [
        ...(hrClubDialogEnabled
          ? [
              {
                label: "Pollen Club RH",
                to: {
                  name: "contact" as const,
                  query: {
                    origin: "main_footer",
                    origin_page_action: "hr_club_apply_cta",
                  },
                },
              },
            ]
          : []),
        {
          label: "Cas clients",
          to: { name: "customer-stories" },
        },
        {
          label: "Blog",
          to: { name: "blog" },
        },
        {
          label: "Méthodologie",
          to: { name: "methodology" },
        },
        {
          label: "Financement par mon employeur",
          to: { name: "getting-financed" },
        },
        {
          label: "Qui sommes-nous ?",
          to: { name: "about-us" },
        },
        {
          label: "Rejoindre l'équipe",
          href: jobsUrl,
        },
        {
          label: "CGU & CGV",
          to: "/content/terms-and-conditions",
        },
        {
          label: "Politique de confidentialité",
          to: "/content/privacy-policy",
        },
        {
          label: "Mentions légales",
          to: "/content/legal-notices",
        },
        {
          label: "Règlement intérieur",
          to: "/content/rules-of-procedure",
        },
        {
          label: "Press & media kit",
          href: pressKit,
        },
      ],
    },
  ];
});
</script>
